import React, { useState, useRef, Fragment } from 'react';
import { AnimatePresence, motion } from 'framer-motion';
import logo from './components/logo.png';

// --- 타입 정의 ---

type TabType = 'creators' | 'brands' | 'influencers';
type LanguageType = 'ko' | 'en';

interface BulletPoint {
  icon: string;
  title: string;
  description: string;
}

interface TabData {
  painPoints: string[];
  solution: string[];
  mainMessage: string;
  subMessage?: string;
  bulletPoints: BulletPoint[];
  existingWayImage?: string;
  existingWayImages?: string[];
  clozetWayVideo?: string;
  clozetWayImage?: string;
  clozetWayOverlay?: string;
  clozetWaySubText?: string;
}

// --- 탭 섹션 데이터 ---

const tabDataKo: Record<TabType, TabData> = {
  creators: {
    painPoints: [
      "콘텐츠는 내가 만들고, 돈은 플랫폼과 광고주만 \n벌어가는 구조에 지치셨나요?",
      "광고주가 찾아올 때까지 기다리지 마세요.",
    ],
    solution: [
      "내가 픽(Pick)한 브랜드 제품을 태그하고, 판매된 만큼 정당한 수익(Commission)을 가져가세요.",
    ],
    mainMessage: "팔로워 10만 패션 계정,\n수익은 아직도 '협찬' 뿐인가요?",
    subMessage: "당신의 감각적인 큐레이션을 이제 투명한 '현금수익'으로 정산받으세요.",
    bulletPoints: [],
  },
  brands: {
    painPoints: [
      "DM, 프로필 링크, 자사몰 검색... 단계가 늘어날 때마다 고객의 90%는 이탈합니다.",
      "광고비를 태워도 실제 어떤 영상에서 구매가 일어났는지 추적할 수 없습니다.",
    ],
    solution: [
      "영상이 곧 매장이 됩니다. 시청 흐름을 끊지 않고 구매를 완료시키세요.",
    ],
    mainMessage: "인스타그램 조회수 10만,\n그런데 왜 자사몰 매출은 제자리일까요?",
    subMessage: "고객에게 '프로필 링크 클릭 → 회원가입 → 상품 검색'의 미로를 강요하지 마세요.",
    existingWayImages: ["/images/brands/brands_existingWay_dms.png", "/images/brands/brands_existingWay_description.png"],
    clozetWayImage: "/images/brands/brands_clozetWay.png",
    bulletPoints: [],
  },
  influencers: {
    painPoints: [
      "정보를 알려줘도 고맙다는 말뿐, 구매는 엉뚱한 최저가 사이트에서 일어납니다.",
      "답변이 늦으면 불친절하다는 오해를 받기도 합니다.",
    ],
    solution: [
      "팬들에게 Clozet으로 이 릴스를 공유하라고 하세요. 팬들은 편해서 좋고, 당신은 수익이 생겨서 좋습니다.",
    ],
    mainMessage: "'언니 이 옷 정보 좀요 ㅠㅠ'\n하루 종일 DM 답장하느라 지치셨죠?",
    subMessage: "일일이 알려주지 마세요. 태그 하나로, 정보 제공과 수익 창출을 동시에 해결하세요.",
    existingWayImage: "/images/influencers/influencer_existingWay.jpg",
    clozetWayVideo: "/videos/Clozet_Demo_InstagramShare_LandingPage_onlyScreen.mp4",
    clozetWayOverlay: "/images/influencers/coffee.png",
    clozetWaySubText: "'이 릴스를 Clozet으로 공유해주세요'\n한줄이면 충분합니다",
    bulletPoints: [],
  },
};

const tabDataEn: Record<TabType, TabData> = {
  creators: {
    painPoints: [
      "Tired of creating content while platforms and advertisers take all the profit?",
      "Don't wait for sponsors to find you.",
    ],
    solution: [
      "Tag the brand products you pick, and earn a fair commission on every sale.",
    ],
    mainMessage: "100K followers on fashion,\nbut income still limited to 'sponsorships'?",
    subMessage: "Turn your curatorial taste into transparent cash revenue.",
    bulletPoints: [],
  },
  brands: {
    painPoints: [
      "DMs, profile links, website searches... with each extra step, 90% of customers drop off.",
      "You spend on ads but can't track which video actually drove the purchase.",
    ],
    solution: [
      "Your video becomes the storefront. Complete the purchase without breaking the viewing flow.",
    ],
    mainMessage: "100K Instagram views,\nso why are your sales stuck?",
    subMessage: "Stop forcing customers through the maze of 'click profile link → sign up → search for product'.",
    existingWayImages: ["/images/brands/brands_existingWay_dms.png", "/images/brands/brands_existingWay_description.png"],
    clozetWayImage: "/images/brands/brands_clozetWay.png",
    bulletPoints: [],
  },
  influencers: {
    painPoints: [
      "You share info, they say thanks, but they buy from the cheapest site elsewhere.",
      "Reply late and they think you're rude.",
    ],
    solution: [
      "Tell your fans to share this Reel on Clozet. They get convenience, you get revenue.",
    ],
    mainMessage: "'Can you tell me about this outfit?'\nExhausted from answering DMs all day?",
    subMessage: "Stop replying one by one. With a single tag, provide info and earn income at once.",
    existingWayImage: "/images/influencers/influencer_existingWay.jpg",
    clozetWayVideo: "/videos/Clozet_Demo_InstagramShare_LandingPage_onlyScreen.mp4",
    clozetWayOverlay: "/images/influencers/coffee.png",
    clozetWaySubText: "'Share this Reel on Clozet'\nOne sentence is all it takes",
    bulletPoints: [],
  },
};

const tabData: Record<LanguageType, Record<TabType, TabData>> = {
  ko: tabDataKo,
  en: tabDataEn,
};

// --- 아이콘 컴포넌트 (Lucide Icons 대체) ---

// 체크 아이콘
const CheckCircleIcon: React.FC<{ className?: string }> = ({ className = "w-6 h-6" }) => (
  <svg
    xmlns="http://www.w3.org/2000/svg"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
    className={className}
  >
    <path d="M22 11.08V12a10 10 0 1 1-5.93-9.14" />
    <polyline points="22 4 12 14.01 9 11.01" />
  </svg>
);

// 모바일 메뉴 아이콘
const MenuIcon: React.FC<{ className?: string }> = ({ className = "w-6 h-6" }) => (
  <svg
    xmlns="http://www.w3.org/2000/svg"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
    className={className}
  >
    <line x1="3" y1="12" x2="21" y2="12" />
    <line x1="3" y1="6" x2="21" y2="6" />
    <line x1="3" y1="18" x2="21" y2="18" />
  </svg>
);

// 닫기 아이콘 (X)
const XIcon: React.FC<{ className?: string }> = ({ className = "w-6 h-6" }) => (
  <svg
    xmlns="http://www.w3.org/2000/svg"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
    className={className}
  >
    <line x1="18" y1="6" x2="6" y2="18" />
    <line x1="6" y1="6" x2="18" y2="18" />
  </svg>
);

// 닫기 (원형) 아이콘 (XCircle)
const XCircleIcon: React.FC<{ className?: string }> = ({ className = "w-6 h-6" }) => (
  <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className={className}>
    <circle cx="12" cy="12" r="10" />
    <line x1="15" y1="9" x2="9" y2="15" />
    <line x1="9" y1="9" x2="15" y2="15" />
  </svg>
);

// 플러스 아이콘 (+)
const PlusIcon: React.FC<{ className?: string }> = ({ className = "w-6 h-6" }) => (
  <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className={className}>
    <line x1="12" y1="5" x2="12" y2="19" />
    <line x1="5" y1="12" x2="19" y2="12" />
  </svg>
);

// 마이너스 아이콘 (-)
const MinusIcon: React.FC<{ className?: string }> = ({ className = "w-6 h-6" }) => (
  <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className={className}>
    <line x1="5" y1="12" x2="19" y2="12" />
  </svg>
);

// Play 아이콘
const PlayIcon: React.FC<{ className?: string }> = ({ className = "w-6 h-6" }) => (
  <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="currentColor" className={className}>
    <polygon points="5,3 19,12 5,21" />
  </svg>
);

// --- 크리에이터 그래프 애니메이션 컴포넌트 (Existing Way) ---
const CreatorGraphAnimation: React.FC = () => {
  const [showBubble, setShowBubble] = useState(false);
  const [followerCount, setFollowerCount] = useState(0);

  React.useEffect(() => {
    // 팔로워 숫자 증가 애니메이션
    const followerInterval = setInterval(() => {
      setFollowerCount((prev) => {
        if (prev >= 100) {
          clearInterval(followerInterval);
          // 그래프 증가가 끝나면 말풍선 표시
          setTimeout(() => setShowBubble(true), 200);
          return 100;
        }
        return prev + 2;
      });
    }, 30);

    return () => {
      clearInterval(followerInterval);
    };
  }, []);

  // 팔로워 그래프 경로 (곡선)
  const followerPath = "M 30 170 Q 100 120, 150 80 T 270 30";

  return (
    <div className="w-full max-w-md mx-auto relative">
      {/* SVG 그래프 */}
      <svg
        viewBox="0 0 300 200"
        className="w-full h-auto"
        style={{ maxHeight: '300px' }}
      >
        {/* 팔로워 그래프 (곡선) */}
        <motion.path
          d={followerPath}
          stroke="black"
          strokeWidth="3"
          fill="none"
          initial={{ pathLength: 0 }}
          animate={{ pathLength: 1 }}
          transition={{ duration: 3, ease: "easeInOut" }}
        />

        {/* 수익 그래프 (빨간색 직선) */}
        <line
          x1="30"
          y1="170"
          x2="270"
          y2="170"
          stroke="#ef4444"
          strokeWidth="3"
        />

        {/* 레이블 */}
        <text x="30" y="195" fontSize="12" fill="black" fontWeight="500">
          팔로워
        </text>
        <text x="230" y="195" fontSize="12" fill="black" fontWeight="500">
          수익
        </text>
      </svg>

      {/* 숫자 애니메이션 */}
      <div className="flex justify-between px-4 mt-2">
        <div className="text-center">
          <span className="text-xl md:text-2xl font-bold text-black">
            {followerCount}K
          </span>
        </div>
        <div className="text-center overflow-hidden h-8">
          <motion.span
            className="text-xl md:text-2xl font-bold text-red-500 inline-block"
            animate={{ rotateX: 360 }}
            transition={{ duration: 1, ease: "easeInOut" }}
          >
            0원
          </motion.span>
        </div>
      </div>

      {/* 말풍선 오버레이 */}
      <AnimatePresence>
        {showBubble && (
          <motion.div
            className="absolute top-40 right-4 md:right-8"
            initial={{ opacity: 0, scale: 0, y: 20 }}
            animate={{ opacity: 1, scale: 1, y: 0 }}
            transition={{ type: "spring", stiffness: 200, damping: 15 }}
          >
            <div className="relative bg-white border-2 border-black rounded-2xl px-4 py-3 shadow-lg">
              <p className="text-base md:text-lg font-bold text-black whitespace-nowrap">
                협찬 수익뿐?
              </p>
              {/* 말풍선 꼬리 */}
              <div
                className="absolute bottom-0 left-8 w-0 h-0"
                style={{
                  transform: 'translateY(100%)',
                  borderLeft: '12px solid transparent',
                  borderRight: '12px solid transparent',
                  borderTop: '12px solid black',
                }}
              />
              <div
                className="absolute bottom-0 left-8 w-0 h-0"
                style={{
                  transform: 'translateY(calc(100% - 2px))',
                  borderLeft: '10px solid transparent',
                  borderRight: '10px solid transparent',
                  borderTop: '10px solid white',
                }}
              />
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};

// --- 클로젯 그래프 애니메이션 컴포넌트 (Clozet Way) ---
const ClozetGraphAnimation: React.FC = () => {
  const [followerCount, setFollowerCount] = useState(0);

  React.useEffect(() => {
    // 팔로워 숫자 증가 애니메이션
    const followerInterval = setInterval(() => {
      setFollowerCount((prev) => {
        if (prev >= 100) {
          clearInterval(followerInterval);
          return 100;
        }
        return prev + 2;
      });
    }, 30);

    return () => {
      clearInterval(followerInterval);
    };
  }, []);

  // 팔로워 그래프 경로 (곡선)
  const followerPath = "M 30 170 Q 100 120, 150 80 T 270 30";
  // 수익 그래프 경로 (곡선, 팔로워보다 조금 낮게)
  const revenuePath = "M 30 170 Q 100 140, 150 110 T 270 70";
  // 면적 채우기를 위한 경로 (수익 그래프 + 하단 라인)
  const revenueAreaPath = "M 30 170 Q 100 140, 150 110 T 270 70 L 270 170 L 30 170 Z";

  return (
    <div className="w-full max-w-md mx-auto relative">
      {/* SVG 그래프 */}
      <svg
        viewBox="0 0 300 200"
        className="w-full h-auto"
        style={{ maxHeight: '300px' }}
      >
        {/* 수익 그래프 면적 (연초록 하이라이트) */}
        <motion.path
          d={revenueAreaPath}
          fill="#86efac"
          fillOpacity="0.3"
          initial={{ pathLength: 0, opacity: 0 }}
          animate={{ pathLength: 1, opacity: 1 }}
          transition={{ duration: 3, ease: "easeInOut" }}
        />

        {/* 팔로워 그래프 (곡선) */}
        <motion.path
          d={followerPath}
          stroke="white"
          strokeWidth="3"
          fill="none"
          initial={{ pathLength: 0 }}
          animate={{ pathLength: 1 }}
          transition={{ duration: 3, ease: "easeInOut" }}
        />

        {/* 수익 그래프 (초록색 곡선) */}
        <motion.path
          d={revenuePath}
          stroke="#22c55e"
          strokeWidth="3"
          fill="none"
          initial={{ pathLength: 0 }}
          animate={{ pathLength: 1 }}
          transition={{ duration: 3, ease: "easeInOut" }}
        />

        {/* 레이블 */}
        <text x="30" y="195" fontSize="12" fill="white" fontWeight="500">
          팔로워
        </text>
        <text x="205" y="195" fontSize="12" fill="white" fontWeight="500">
          수익(커미션)
        </text>
      </svg>

      {/* 숫자 애니메이션 */}
      <div className="flex justify-between px-4 mt-2">
        <div className="text-center">
          <span className="text-xl md:text-2xl font-bold text-white">
            {followerCount}K
          </span>
        </div>
        <div className="text-center">
          <span className="text-xl md:text-2xl font-bold text-green-400">
            ???
          </span>
        </div>
      </div>
    </div>
  );
};


// --- 1. Navbar 컴포넌트 ---

interface NavbarProps {
  activeTab: TabType | null;
  setActiveTab: (tab: TabType) => void;
  scrollToTabs: () => void;
  language: LanguageType;
  setLanguage: (lang: LanguageType) => void;
}

const Navbar: React.FC<NavbarProps> = ({ activeTab, setActiveTab, scrollToTabs, language, setLanguage }) => {
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);

  const navItems: { id: TabType; label: string }[] = [
    { id: 'creators', label: 'Creators' },
    { id: 'brands', label: 'Brands' },
    { id: 'influencers', label: 'Influencers' },
  ];

  const handleNavClick = (tab: TabType) => {
    setActiveTab(tab);
    scrollToTabs();
    setIsMobileMenuOpen(false);
  };

  return (
    <>
      {/* 데스크탑 네비게이션 */}
      <nav className="fixed top-0 left-0 right-0 z-50 bg-white bg-opacity-80 backdrop-blur-md border-b border-gray-200">
        <div className="container mx-auto px-6 md:px-16 py-5 flex justify-between items-center">
          {/* 좌측: 로고 */}
          <img
            src={logo}
            alt="Clozet"
            className="h-10 md:h-12 cursor-pointer"
            onClick={() => window.scrollTo({ top: 0, behavior: 'smooth' })}
          />

          {/* 중앙: 텍스트 버튼 (데스크탑) */}
          <div className="hidden md:flex items-center gap-10">
            {navItems.map((item) => (
              <button
                key={item.id}
                onClick={() => handleNavClick(item.id)}
                className={`
                  text-lg font-light transition-colors
                  ${activeTab === item.id ? 'text-black' : 'text-gray-400 hover:text-black'}
                `}
              >
                {item.label}
              </button>
            ))}

            {/* 언어 전환 버튼 */}
            <div className="flex items-center gap-2">
              <button
                onClick={() => setLanguage('ko')}
                className={`px-3 py-1 text-sm font-light transition-colors ${
                  language === 'ko' ? 'text-black font-medium' : 'text-gray-400 hover:text-black'
                }`}
              >
                KO
              </button>
              <span className="text-gray-300">|</span>
              <button
                onClick={() => setLanguage('en')}
                className={`px-3 py-1 text-sm font-light transition-colors ${
                  language === 'en' ? 'text-black font-medium' : 'text-gray-400 hover:text-black'
                }`}
              >
                EN
              </button>
            </div>

            {/* 파트너 로그인 버튼 */}
            <a
              href="https://dashboard.clozet.my/login"
              className="px-5 py-2 text-sm font-light text-gray-500 border border-gray-300 rounded-full hover:text-black hover:border-gray-400 transition-colors"
            >
              {language === 'ko' ? '파트너 로그인' : 'Partner Login'}
            </a>
          </div>

          {/* 우측: 모바일 메뉴 토글 */}
          <div className="md:hidden">
            <button onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)} className="text-black">
              {isMobileMenuOpen ? <XIcon /> : <MenuIcon />}
            </button>
          </div>
        </div>
      </nav>

      {/* 모바일 메뉴 (Framer Motion) */}
      <AnimatePresence>
        {isMobileMenuOpen && (
          <motion.div
            initial={{ opacity: 0, y: -50 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -50 }}
            transition={{ duration: 0.3, ease: "easeInOut" }}
            className="md:hidden fixed top-0 left-0 right-0 bottom-0 bg-white z-40 pt-20"
          >
            <div className="flex flex-col items-center justify-center h-full gap-8">
              {navItems.map((item) => (
                <button
                  key={item.id}
                  onClick={() => handleNavClick(item.id)}
                  className={`
                    text-3xl font-light transition-colors
                    ${activeTab === item.id ? 'text-black' : 'text-gray-400 hover:text-black'}
                  `}
                >
                  {item.label}
                </button>
              ))}

              {/* 언어 전환 버튼 (모바일) */}
              <div className="flex items-center gap-4">
                <button
                  onClick={() => setLanguage('ko')}
                  className={`px-4 py-2 text-xl font-light transition-colors ${
                    language === 'ko' ? 'text-black font-medium' : 'text-gray-400 hover:text-black'
                  }`}
                >
                  KO
                </button>
                <span className="text-gray-300">|</span>
                <button
                  onClick={() => setLanguage('en')}
                  className={`px-4 py-2 text-xl font-light transition-colors ${
                    language === 'en' ? 'text-black font-medium' : 'text-gray-400 hover:text-black'
                  }`}
                >
                  EN
                </button>
              </div>

              {/* 파트너 로그인 버튼 (모바일) */}
              <a
                href="https://dashboard.clozet.my/login"
                className="px-6 py-3 text-lg font-light text-gray-500 border border-gray-300 rounded-full hover:text-black hover:border-gray-400 transition-colors"
              >
                {language === 'ko' ? '파트너 로그인' : 'Partner Login'}
              </a>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
};

// --- 2. Hero 컴포넌트 (수정됨) ---

const heroText: Record<LanguageType, {
  mainTitle: JSX.Element;
  subTitle: JSX.Element;
  description: JSX.Element;
  ctaButton: string;
  selectPrompt: string;
  creatorButton: string;
  brandButton: string;
  influencerButton: string;
}> = {
  ko: {
    mainTitle: <>인스타그램 Reels 조회수 10만 <br />그런데 왜 매출은 낮을까요 ?</>,
    subTitle: <>"소비자는 구매과정이 <b>귀찮으면</b> 구매를 포기합니다"</>,
    description: <>댓글 문의, DM 노가다, 프로필 링크등 귀찮은 과정 없이. <br />영상 속 상품을 발견 즉시 구매로 연결하는 숏폼 커머스 솔루션.</>,
    ctaButton: "입점 문의하기",
    selectPrompt: "당신에게 맞는 솔루션을 확인하세요",
    creatorButton: "크리에이터 / 매거진",
    brandButton: "브랜드",
    influencerButton: "인플루언서",
  },
  en: {
    mainTitle: <>100K views on Instagram Reels <br />So why are sales still low?</>,
    subTitle: <>"Consumers <b>give up</b> when the purchase process is too much work"</>,
    description: <>No comment inquiries, DM hassles, or profile link clicks. <br />A short-form commerce solution that converts product discovery into instant purchases.</>,
    ctaButton: "Inquire Now",
    selectPrompt: "Find the solution that fits you",
    creatorButton: "Creator / Magazine",
    brandButton: "Brand",
    influencerButton: "Influencer",
  },
};

interface HeroProps {
  activeTab: TabType | null;
  setActiveTab: (tab: TabType) => void;
  scrollToTabs: () => void;
  language: LanguageType;
}

const Hero: React.FC<HeroProps> = ({ activeTab, setActiveTab, scrollToTabs, language }) => {

  const handleButtonClick = (tab: TabType) => {
    setActiveTab(tab);
    scrollToTabs();
  };

  const currentText = heroText[language];

  return (
    <motion.section
      className={`pt-40 pb-20 md:pt-64 md:pb-32 flex flex-col justify-center items-center text-center container mx-auto px-6 ${
        activeTab === null ? 'min-h-screen' : 'min-h-[80vh]'
      }`}
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.8 }}
    >
      {/* 메인 메시지 */}
      <h1 className="text-5xl sm:text-6xl md:text-7xl lg:text-8xl font-bold text-black tracking-tighter leading-tight">
        {currentText.mainTitle}
      </h1>

      {/* 중간 메시지 */}
      <p className="mt-8 text-2xl md:text-4xl text-black max-w-3xl mx-auto font-normal leading-relaxed">
        {currentText.subTitle}
      </p>

      {/* 서브 메시지 */}
      <p className="mt-8 text-lg md:text-2xl text-gray-600 max-w-3xl mx-auto font-light leading-relaxed">
        {currentText.description}
      </p>

      {/* CTA 버튼 */}
      <motion.a
        href="https://tally.so/r/zxMWQM"
        target="_blank"
        rel="noopener noreferrer"
        className="mt-10 inline-block text-lg font-light py-3 px-10 bg-black text-white rounded-full transition-colors hover:bg-gray-800"
        whileHover={{ scale: 1.05 }}
        whileTap={{ scale: 0.95 }}
      >
        {currentText.ctaButton}
      </motion.a>

      {/* === 새로 추가된 영상/GIF 섹션 === */}
      <motion.div
        className="mt-12 w-full max-w-4xl"
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.8, delay: 0.2 }}
      >
        <div className="bg-gray-100 rounded-xl shadow-lg aspect-video flex items-center justify-center">
          <video
            src="/videos/Clozet_Demo_InstagramShare_LandingPage_horizontal.mp4"
            autoPlay
            muted
            loop
            playsInline
            onLoadedMetadata={(e) => {
              const video = e.target as HTMLVideoElement;
              video.playbackRate = 1;
            }}
            className="w-full h-full rounded-xl object-cover"
          />
        </div>
      </motion.div>
      {/* === 끝 === */}


      {/* 버튼 섹션 */}
      <div className="mt-24 flex flex-col items-center justify-center gap-8">
        {/* 안내 문구 */}
        {activeTab === null && (
          <motion.div
            className="text-center max-w-2xl"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5 }}
          >
            <p className="text-xl md:text-2xl text-gray-600 font-light leading-relaxed">
              {currentText.selectPrompt}
            </p>
          </motion.div>
        )}

        <div className="flex flex-col sm:flex-row gap-4">
          <motion.button
            onClick={() => handleButtonClick('creators')}
            className={`text-base md:text-lg py-4 px-10 rounded-full transition-all ${
              activeTab === 'creators'
                ? 'bg-black text-white font-semibold shadow-xl'
                : 'bg-white text-black font-medium border-2 border-gray-300 hover:border-gray-500'
            }`}
            animate={activeTab === null ? {
              scale: [1, 1.05, 1, 1, 1],
            } : {}}
            transition={{
              duration: 3,
              repeat: Infinity,
              delay: 0,
              ease: "easeInOut",
              times: [0, 0.33, 0.5, 0.66, 1]
            }}
            whileHover={{ scale: 1.05 }}
            whileTap={{ scale: 0.95 }}
          >
            {currentText.creatorButton}
          </motion.button>
          <motion.button
            onClick={() => handleButtonClick('brands')}
            className={`text-base md:text-lg py-4 px-10 rounded-full transition-all ${
              activeTab === 'brands'
                ? 'bg-black text-white font-semibold shadow-xl'
                : 'bg-white text-black font-medium border-2 border-gray-300 hover:border-gray-500'
            }`}
            animate={activeTab === null ? {
              scale: [1, 1, 1.05, 1, 1],
            } : {}}
            transition={{
              duration: 3,
              repeat: Infinity,
              delay: 0,
              ease: "easeInOut",
              times: [0, 0.33, 0.5, 0.66, 1]
            }}
            whileHover={{ scale: 1.05 }}
            whileTap={{ scale: 0.95 }}
          >
            {currentText.brandButton}
          </motion.button>
          <motion.button
            onClick={() => handleButtonClick('influencers')}
            className={`text-base md:text-lg py-4 px-10 rounded-full transition-all ${
              activeTab === 'influencers'
                ? 'bg-black text-white font-semibold shadow-xl'
                : 'bg-white text-black font-medium border-2 border-gray-300 hover:border-gray-500'
            }`}
            animate={activeTab === null ? {
              scale: [1, 1, 1, 1.05, 1],
            } : {}}
            transition={{
              duration: 3,
              repeat: Infinity,
              delay: 0,
              ease: "easeInOut",
              times: [0, 0.33, 0.5, 0.66, 1]
            }}
            whileHover={{ scale: 1.05 }}
            whileTap={{ scale: 0.95 }}
          >
            {currentText.influencerButton}
          </motion.button>
        </div>
      </div>
    </motion.section>
  );
};

// --- 3. 탭 섹션 컴포넌트 ---
interface TabsSectionProps {
  activeTab: TabType;
  tabsSectionRef: React.RefObject<HTMLDivElement>;
  language: LanguageType;
}

const TabsSection: React.FC<TabsSectionProps> = ({ activeTab, tabsSectionRef, language }) => {
  const currentData = tabData[language][activeTab];

  return (
    <section ref={tabsSectionRef} className="py-20 md:py-32 bg-white overflow-hidden">
      <div className="container mx-auto px-6 md:px-16">
        <AnimatePresence mode="wait">
          <motion.div
            key={activeTab} // 탭이 변경될 때마다 Framer Motion이 감지
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -30 }}
            transition={{ duration: 0.5, ease: "easeInOut" }}
          >
            {/* 슬라이드용 메시지 */}
            <div className="text-center max-w-5xl mx-auto">
              <h2 className="text-3xl sm:text-4xl md:text-6xl font-bold text-black tracking-tighter leading-tight whitespace-pre-line">
                {currentData.mainMessage}
              </h2>
              {currentData.subMessage && (
                <p className="mt-6 text-xl sm:text-2xl md:text-3xl text-gray-600 font-light leading-relaxed">
                  {activeTab === 'influencers' ? (
                    <>
                      일일이 알려주지 마세요. 태그 하나로, 정보 제공과 <br /><strong className="text-black">협찬이 없을때도</strong> 수익 창출을 동시에 해결하세요.
                    </>
                  ) : activeTab === 'creators' ? (
                    <>
                      당신의 감각적인 큐레이션을 이제 투명한 <strong className="text-black">'현금수익'</strong>으로 정산받으세요.
                    </>
                  ) : (
                    currentData.subMessage
                  )}
                </p>
              )}
            </div>

            {/* Existing Way / Clozet Way 그리드 */}
            <div className={`mt-16 md:mt-24 ${activeTab === 'brands' ? 'flex flex-col gap-8 md:gap-12' : 'grid md:grid-cols-2 gap-8 md:gap-16 items-stretch'}`}>
              {/* Existing Way */}
              <motion.div
                className="border border-gray-200 rounded-2xl p-8 md:p-12 bg-white flex flex-col h-full"
                initial={{ opacity: 0, x: activeTab === 'brands' ? 0 : -20, y: activeTab === 'brands' ? -20 : 0 }}
                animate={{ opacity: 1, x: 0, y: 0 }}
                transition={{ duration: 0.5, delay: 0.2 }}
              >
                <h3 className="text-2xl md:text-3xl font-semibold text-gray-500 tracking-tight mb-8">Existing Way</h3>

                {/* 크리에이터 탭일 때 그래프 표시 */}
                {activeTab === 'creators' && (
                  <div className="mb-8">
                    <CreatorGraphAnimation />
                  </div>
                )}

                {currentData.existingWayImage && (
                  <div className="mb-8">
                    <img
                      src={currentData.existingWayImage}
                      alt="Existing Way"
                      className="w-full h-auto rounded-lg"
                    />
                  </div>
                )}
                {currentData.existingWayImages && (
                  <div className="mb-8 space-y-6">
                    {currentData.existingWayImages.map((imgSrc, index) => (
                      <img
                        key={index}
                        src={imgSrc}
                        alt={`Existing Way ${index + 1}`}
                        className="w-full h-auto rounded-lg"
                      />
                    ))}
                  </div>
                )}
                <ul className="space-y-6 mt-auto pt-6">
                  {currentData.painPoints.map((item, i) => (
                    <li key={i} className="flex items-start gap-4">
                      <XCircleIcon className="w-7 h-7 text-red-500 flex-shrink-0 mt-0.5" />
                      <span className="text-lg md:text-xl text-black font-medium leading-relaxed whitespace-pre-line">{item}</span>
                    </li>
                  ))}
                </ul>
              </motion.div>

              {/* Clozet Way */}
              <motion.div
                className="border-2 border-black rounded-2xl p-8 md:p-12 bg-black text-white relative overflow-hidden flex flex-col h-full"
                initial={{ opacity: 0, x: 20 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ duration: 0.5, delay: 0.3 }}
              >
                <h3 className="text-3xl md:text-4xl font-semibold text-white tracking-tight mb-8">Clozet Way</h3>

                {/* 크리에이터 탭일 때 그래프 표시 */}
                {activeTab === 'creators' && (
                  <div className="mb-8">
                    <ClozetGraphAnimation />
                  </div>
                )}

                {currentData.clozetWaySubText && (
                  <p className="text-white text-center text-lg md:text-xl font-light mb-6 whitespace-pre-line">
                    {currentData.clozetWaySubText}
                  </p>
                )}
                {currentData.clozetWayVideo && (
                  <div className="mb-4 flex justify-start">
                    {/* 아이폰 목업 프레임 */}
                    <div className="relative w-[220px] md:w-[260px] bg-black rounded-[40px] p-3 shadow-2xl">
                      {/* 노치 */}
                      <div className="absolute top-0 left-1/2 transform -translate-x-1/2 w-32 h-6 bg-black rounded-b-2xl z-10"></div>

                      {/* 스크린 영역 */}
                      <div className="relative bg-white rounded-[32px] overflow-hidden">
                        <video
                          src={currentData.clozetWayVideo}
                          autoPlay
                          muted
                          loop
                          playsInline
                          className="w-full h-auto"
                        />
                      </div>
                    </div>
                  </div>
                )}
                {currentData.clozetWayImage && (
                  <div className="mb-8">
                    <img
                      src={currentData.clozetWayImage}
                      alt="Clozet Way"
                      className="w-full h-auto rounded-lg"
                    />
                  </div>
                )}
                <ul className="space-y-6 mt-auto pt-6 relative z-10">
                  {currentData.solution.map((item, i) => (
                    <li key={i} className="flex items-start gap-4">
                      <CheckCircleIcon className="w-7 h-7 text-green-400 flex-shrink-0 mt-0.5" />
                      <span className="text-lg md:text-xl text-white font-semibold leading-relaxed">{item}</span>
                    </li>
                  ))}
                </ul>
                {currentData.clozetWayOverlay && (
                  <img
                    src={currentData.clozetWayOverlay}
                    alt="Overlay"
                    className="absolute bottom-20 right-0 w-20 h-20 md:w-60 md:h-60 object-contain"
                  />
                )}
              </motion.div>
            </div>

            {/* Bullet Points */}
            <motion.div
              className="mt-16 md:mt-24 max-w-4xl mx-auto space-y-8"
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5, delay: 0.4 }}
            >
              {currentData.bulletPoints.map((point, index) => (
                <div key={index} className="flex items-start gap-4">
                  <span className="text-3xl flex-shrink-0">{point.icon}</span>
                  <div>
                    <h4 className="text-xl md:text-2xl font-semibold text-black mb-2">{point.title}:</h4>
                    <p className="text-lg md:text-xl text-gray-600 font-light leading-relaxed">"{point.description}"</p>
                  </div>
                </div>
              ))}
            </motion.div>
          </motion.div>
        </AnimatePresence>
      </div>
    </section>
  );
};

// --- 4. 파트너 로고 섹션 (새로 추가) ---

// const PartnerLogos: React.FC = () => {
//   // 로고 텍스트 대신 실제 로고 이미지를 사용하세요
//   const logos = ["BRAND 1", "BRAND 2", "BRAND 3", "BRAND 4", "BRAND 5", "BRAND 6"];
//   return (
//     <section className="py-16 md:py-24 bg-white">
//       <div className="container mx-auto px-6 md:px-16">
//         <motion.h3
//           className="text-center text-base md:text-lg font-light text-gray-500 tracking-widest uppercase mb-12"
//           initial={{ opacity: 0 }}
//           whileInView={{ opacity: 1 }}
//           viewport={{ once: true, amount: 0.5 }}
//           transition={{ duration: 0.7 }}
//         >
//           Currently with Clozet
//         </motion.h3>
//         <div className="flex flex-wrap justify-center items-center gap-x-12 sm:gap-x-16 gap-y-8">
//           {logos.map((logo) => (
//             <motion.div
//               key={logo}
//               initial={{ opacity: 0, y: 20 }}
//               whileInView={{ opacity: 1, y: 0 }}
//               viewport={{ once: true, amount: 0.3 }}
//               transition={{ duration: 0.5 }}
//             >
//               {/* 실제 로고 이미지로 교체하세요 (예: <img src="/logos/brand1.svg" alt="Brand 1" className="h-8 grayscale" />) */}
//               <span className="text-2xl sm:text-3xl font-bold text-gray-300 hover:text-gray-500 transition-colors cursor-pointer">
//                 {logo}
//               </span>
//             </motion.div>
//           ))}
//         </div>
//       </div>
//     </section>
//   );
// };

// --- 5. PhoneMockup & FeatureSection ---

// --- 5a. PhoneMockup 컴포넌트 (수정됨) ---
const PhoneMockup: React.FC = () => {
  const [isCardVisible, setIsCardVisible] = useState(false);

  return (
    <div className="relative w-full max-w-[320px] mx-auto">
      {/* 아이폰 목업 이미지 */}
      <div className="relative">
        <img
          src="/images/mockups/iphone_mockUp.png"
          alt="iPhone Mockup"
          className="w-full h-auto object-contain"
        />

        {/* Play 버튼 (Framer Motion) - 스티커 없을 때만 표시 */}
        {!isCardVisible && (
          <motion.button
            className="absolute z-10"
            style={{
              bottom: '33.33%',
              left: '43%',
              transform: 'translateX(-50%)',
            }}
            onClick={() => setIsCardVisible(true)}
            animate={{ scale: [1, 1.1, 1] }}
            transition={{ duration: 1.5, repeat: Infinity, ease: "easeInOut" }}
          >
            <span className="relative flex items-center justify-center w-12 h-12">
              <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-white opacity-75"></span>
              <span className="relative z-10 text-white">
                <PlayIcon className="w-8 h-8" />
              </span>
            </span>
          </motion.button>
        )}

        {/* 스티커 이미지 인터랙션 (Framer Motion) */}
        <AnimatePresence>
          {isCardVisible && (
            <motion.div
              className="absolute z-20"
              style={{
                bottom: '33.33%',
                left: '10%',
                transform: 'translateX(-50%)',
              }}
              initial={{ opacity: 0, scale: 0 }}
              animate={{ opacity: 1, scale: 1 }}
              exit={{ opacity: 0, scale: 0 }}
              transition={{ type: "spring", stiffness: 300, damping: 30 }}
            >
              <div className="relative">
                <img
                  src="/images/mockups/iphone_sticker.png"
                  alt="Product Sticker"
                  className="w-36 h-auto object-contain"
                />
                <button
                  onClick={() => setIsCardVisible(false)}
                  className="absolute -top-2 -right-2 bg-white rounded-full p-1 text-gray-600 hover:text-black shadow-lg"
                >
                  <XIcon className="w-4 h-4" />
                </button>
              </div>
            </motion.div>
          )}
        </AnimatePresence>

      </div>
    </div>
  );
};

// --- 5b. 노트북 프레임 컴포넌트 ---
interface LaptopFrameProps {
  imageSrc?: string;
}

const LaptopFrame: React.FC<LaptopFrameProps> = ({ imageSrc }) => {
  return (
    <div className="relative w-full max-w-[700px] mx-auto">
      {/* 노트북 화면 */}
      <div className="relative bg-gradient-to-b from-gray-800 to-gray-900 rounded-t-3xl p-3 shadow-2xl border border-gray-700">
        {/* 상단 베젤 (카메라 영역) */}
        <div className="absolute top-0 left-1/2 transform -translate-x-1/2 flex items-center justify-center">
          <div className="w-2 h-2 bg-gray-600 rounded-full mt-1.5"></div>
        </div>

        {/* 스크린 영역 */}
        <div className="bg-white rounded-xl overflow-hidden aspect-video shadow-inner">
          {imageSrc ? (
            <img src={imageSrc} alt="Screen Content" className="w-full h-full object-cover" />
          ) : (
            /* 이미지 추가 공간 */
            <div className="w-full h-full bg-gradient-to-br from-gray-50 to-gray-100 flex items-center justify-center border border-gray-200">
              <div className="text-center">
                <div className="w-16 h-16 mx-auto mb-3 bg-gray-200 rounded-lg flex items-center justify-center">
                  <svg className="w-8 h-8 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z" />
                  </svg>
                </div>
                <p className="text-sm text-gray-400 font-light">화면 이미지 추가</p>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* 노트북 베이스 */}
      <div className="relative">
        <div className="h-2 bg-gradient-to-b from-gray-700 to-gray-800"></div>
        <div className="h-4 bg-gradient-to-b from-gray-800 to-gray-900 rounded-b-3xl shadow-xl"></div>
      </div>

      {/* 노트북 하단 받침대 */}
      <div className="relative h-1 bg-gray-400 mx-auto mt-0.5 shadow-md" style={{ width: '60%', borderRadius: '0 0 50% 50%' }}></div>
    </div>
  );
};

// --- 5c. 아이폰 프레임 전용 컴포넌트 ---
interface IPhoneFrameProps {
  imageSrc?: string;
}

const IPhoneFrame: React.FC<IPhoneFrameProps> = ({ imageSrc }) => {
  return (
    <div className="relative w-full max-w-[320px] mx-auto">
      {/* 아이폰 목업 프레임 */}
      <div className="relative bg-gradient-to-br from-gray-800 via-black to-gray-900 rounded-[50px] p-4 shadow-2xl">
        {/* 노치 */}
        <div className="absolute top-0 left-1/2 transform -translate-x-1/2 w-36 h-7 bg-black rounded-b-3xl z-10 shadow-lg"></div>

        {/* 음량/전원 버튼 디테일 */}
        <div className="absolute left-0 top-24 w-1 h-12 bg-gray-700 rounded-r-full"></div>
        <div className="absolute left-0 top-40 w-1 h-8 bg-gray-700 rounded-r-full"></div>
        <div className="absolute right-0 top-32 w-1 h-16 bg-gray-700 rounded-l-full"></div>

        {/* 스크린 영역 */}
        <div className="relative bg-white rounded-[42px] overflow-hidden aspect-[9/19.5] shadow-inner border border-gray-800">
          {imageSrc ? (
            <img src={imageSrc} alt="Screen Content" className="w-full h-full object-cover" />
          ) : (
            /* 이미지 추가 공간 */
            <div className="w-full h-full bg-gradient-to-br from-gray-50 to-gray-100 flex items-center justify-center">
              <div className="text-center px-6">
                <div className="w-14 h-14 mx-auto mb-3 bg-gray-200 rounded-2xl flex items-center justify-center">
                  <svg className="w-7 h-7 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z" />
                  </svg>
                </div>
                <p className="text-xs text-gray-400 font-light">화면 이미지 추가</p>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

// --- 5d. Feature 섹션 컴포넌트 ---
interface FeatureSectionProps {
  activeTab: TabType | null;
  stepNumber?: number;
  language: LanguageType;
}

const FeatureSection: React.FC<FeatureSectionProps> = ({ activeTab, stepNumber = 1 }) => {
  const features = [
    "Reels 기반의 몰입형 쇼핑 경험",
    "영상 속 원하는 위치에 상품 태그 스티커",
    "스크린 이탈 없는 즉시 구매 가능한 UX",
    "모든 디바이스에 최적화된 자동 스케일 조정",
    "상대 좌표 기반 오버레이",
    "성과 측정을 위한 상세 데이터 대시보드 제공",
  ];

  // 크리에이터, 인플루언서, 브랜드 탭일 때
  if (activeTab === 'creators' || activeTab === 'influencers' || activeTab === 'brands') {
    const bgColor = stepNumber % 2 === 1 ? 'bg-gray-50' : 'bg-white';

    // 이미지 경로 설정
    const getImageSrc = () => {
      if (activeTab === 'brands') {
        return '/screenshots/brands_marketplace_laptop.png';
      }
      return '/screenshots/brands_marketplace_laptop.png';
    };

    // 타이틀 텍스트 결정
    const getTitleText = () => {
      if (activeTab === 'brands') {
        return (
          <>
            상품만 등록하세요.<br />세일즈는<span className="text-transparent bg-clip-text bg-gradient-to-r from-purple-600 to-blue-600">컨텐츠가</span>가 대신합니다
          </>
        );
      }
      if (activeTab === 'influencers') {
        return (
          <>
            '이 옷 뭐예요?' 수백 번 묻는 팬들에게,<br /><span className="text-transparent bg-clip-text bg-gradient-to-r from-purple-600 to-blue-600">"이 릴스를 Clozet으로 공유해주세요"</span><br />문장 하나로 답장하세요.
          </>
        );
      }
      return (
        <>
          재고 걱정, 배송 부담 없이<br />당신의 <span className="text-transparent bg-clip-text bg-gradient-to-r from-purple-600 to-blue-600">'안목'</span>을 팝니다
        </>
      );
    };

    return (
      <section className={`py-20 md:py-32 ${bgColor}`}>
        <div className="container mx-auto px-6 md:px-16">
          {/* 상단 타이틀 */}
          {stepNumber === 1 && (
            <motion.h2
              className="text-4xl sm:text-5xl md:text-6xl font-bold text-black tracking-tighter leading-tight text-center mb-20"
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, amount: 0.5 }}
              transition={{ duration: 0.7 }}
            >
              {getTitleText()}
            </motion.h2>
          )}

          <div className="grid lg:grid-cols-2 gap-16 lg:gap-24 items-center">
            {/* 좌측: 텍스트 */}
            <motion.div
              initial={{ opacity: 0, x: -30 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true, amount: 0.5 }}
              transition={{ duration: 0.7 }}
            >
              <div className="space-y-6">
                <div className="inline-block px-4 py-2 bg-black text-white rounded-full text-sm font-medium mb-4">
                  Step {stepNumber}
                </div>
                {activeTab === 'brands' ? (
                  <>
                    <h3 className="text-2xl md:text-3xl font-bold text-black tracking-tight leading-tight mb-3">
                      Commission Marketplace
                    </h3>
                    <h4 className="text-3xl md:text-4xl font-bold text-black tracking-tight leading-tight">
                      성과형 파트너십
                    </h4>
                    <p className="text-xl md:text-2xl text-gray-600 font-light leading-relaxed whitespace-pre-line">
                      광고비 선지출은 그만. 마켓플레이스에 상품과 커미션율만 공개하면, <strong className="text-black">판매가 발생했을 때만 비용(수수료)을 지불</strong>합니다.
                    </p>
                  </>
                ) : (
                  <>
                    <h3 className="text-2xl md:text-3xl font-bold text-black tracking-tight leading-tight mb-3">
                      Affiliate Lounge
                    </h3>
                    <h4 className="text-3xl md:text-4xl font-bold text-black tracking-tight leading-tight">
                      골라 담기만 하세요
                    </h4>
                    <p className="text-xl md:text-2xl text-gray-600 font-light leading-relaxed whitespace-pre-line">
                      마음에 드는 브랜드 상품을 골라 담기만 하세요.{'\n'}
                      <strong className="text-black">사입, 재고, 배송, CS는 브랜드가 담당</strong>하고{'\n'}
                      당신은 판매 수익만 챙기면 됩니다.
                    </p>
                  </>
                )}
              </div>
            </motion.div>

            {/* 우측: 노트북 프레임 */}
            <motion.div
              initial={{ opacity: 0, scale: 0.9 }}
              whileInView={{ opacity: 1, scale: 1 }}
              viewport={{ once: true, amount: 0.5 }}
              transition={{ duration: 0.7 }}
            >
              <LaptopFrame imageSrc={getImageSrc()} />
            </motion.div>
          </div>
        </div>
      </section>
    );
  }

  // 기존 렌더링 (다른 탭일 때)
  return (
    <section className="py-20 md:py-32 bg-gray-50">
      <div className="container mx-auto px-6 md:px-16 grid lg:grid-cols-2 gap-16 lg:gap-24 items-center">
        {/* 좌측: 텍스트 */}
        <motion.div
          initial={{ opacity: 0, x: -30 }}
          whileInView={{ opacity: 1, x: 0 }}
          viewport={{ once: true, amount: 0.5 }}
          transition={{ duration: 0.7 }}
        >
          <h2 className="text-4xl sm:text-5xl md:text-6xl font-bold text-black tracking-tighter leading-tight">
            영상 속 모든 순간이<br />
            구매가 됩니다.
          </h2>
          <p className="mt-6 text-lg md:text-xl text-gray-600 max-w-xl font-light leading-relaxed">
            Clozet은 시청자가 영상을 즐기는 동시에 자연스럽게 상품을 발견하고 구매할 수 있는 가장 직관적인 방법을 제안합니다.
          </p>
          <ul className="mt-10 space-y-4">
            {features.map((feature, index) => (
              <li key={index} className="flex items-start gap-3">
                <CheckCircleIcon className="w-6 h-6 text-blue-500 flex-shrink-0 mt-1" />
                <span className="text-lg md:text-xl text-black font-light leading-relaxed">
                  {feature}
                </span>
              </li>
            ))}
          </ul>
        </motion.div>

        {/* 우측: 폰 목업 */}
        <motion.div
          initial={{ opacity: 0, scale: 0.9 }}
          whileInView={{ opacity: 1, scale: 1 }}
          viewport={{ once: true, amount: 0.5 }}
          transition={{ duration: 0.7 }}
        >
          <PhoneMockup />
        </motion.div>
      </div>
    </section>
  );
};

// --- 5e. Feature 섹션 2 (두 번째 섹션) ---
const FeatureSection2: React.FC<FeatureSectionProps> = ({ activeTab, stepNumber = 2 }) => {
  const features = [
    "Reels 기반의 몰입형 쇼핑 경험",
    "영상 속 원하는 위치에 상품 태그 스티커",
    "스크린 이탈 없는 즉시 구매 가능한 UX",
    "모든 디바이스에 최적화된 자동 스케일 조정",
    "상대 좌표 기반 오버레이",
    "성과 측정을 위한 상세 데이터 대시보드 제공",
  ];

  // 크리에이터, 인플루언서, 브랜드 탭일 때
  if (activeTab === 'creators' || activeTab === 'influencers' || activeTab === 'brands') {
    const bgColor = stepNumber % 2 === 1 ? 'bg-gray-50' : 'bg-white';

    // 이미지 경로 설정
    const imageSrc = '/screenshots/tagging_iphone.png';

    return (
      <section className={`py-20 md:py-32 ${bgColor}`}>
        <div className="container mx-auto px-6 md:px-16">
          <div className="grid lg:grid-cols-2 gap-16 lg:gap-24 items-center">
            {/* 좌측: 아이폰 프레임 */}
            <motion.div
              initial={{ opacity: 0, scale: 0.9 }}
              whileInView={{ opacity: 1, scale: 1 }}
              viewport={{ once: true, amount: 0.5 }}
              transition={{ duration: 0.7 }}
            >
              <IPhoneFrame imageSrc={imageSrc} />
            </motion.div>

            {/* 우측: 텍스트 */}
            <motion.div
              initial={{ opacity: 0, x: 30 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true, amount: 0.5 }}
              transition={{ duration: 0.7 }}
            >
              <div className="space-y-6">
                <div className="inline-block px-4 py-2 bg-black text-white rounded-full text-sm font-medium mb-4">
                  Step {stepNumber}
                </div>
                <h3 className="text-2xl md:text-3xl font-bold text-black tracking-tight leading-tight mb-3">
                  Invisible Tagging
                </h3>
                <h4 className="text-3xl md:text-4xl font-bold text-black tracking-tight leading-tight">
                  감성은 그대로, 태그만 추가
                </h4>
                <p className="text-xl md:text-2xl text-gray-600 font-light leading-relaxed whitespace-pre-line">
                  콘텐츠의 감성을 해치는 링크는 이제 그만.{'\n'}
                  영상 속 상품 위치에 정확히 붙는{'\n'}
                  <strong className="text-black">투명 태그</strong>로 스타일을 유지하며 정보를 제공하세요.
                </p>
                <div className="flex items-center gap-3 pt-4">
                  <CheckCircleIcon className="w-6 h-6 text-green-500 flex-shrink-0" />
                  <span className="text-lg text-gray-600">영상 속 원하는 위치에 정확히 배치</span>
                </div>
                <div className="flex items-center gap-3">
                  <CheckCircleIcon className="w-6 h-6 text-green-500 flex-shrink-0" />
                  <span className="text-lg text-gray-600">시청 흐름을 방해하지 않는 투명 디자인</span>
                </div>
              </div>
            </motion.div>
          </div>
        </div>
      </section>
    );
  }

  // 기존 렌더링 (다른 탭일 때)
  return (
    <section className="py-20 md:py-32 bg-white">
      <div className="container mx-auto px-6 md:px-16 grid lg:grid-cols-2 gap-16 lg:gap-24 items-center">
        {/* 좌측: 폰 목업 */}
        <motion.div
          initial={{ opacity: 0, scale: 0.9 }}
          whileInView={{ opacity: 1, scale: 1 }}
          viewport={{ once: true, amount: 0.5 }}
          transition={{ duration: 0.7 }}
        >
          <PhoneMockup />
        </motion.div>

        {/* 우측: 텍스트 */}
        <motion.div
          initial={{ opacity: 0, x: 30 }}
          whileInView={{ opacity: 1, x: 0 }}
          viewport={{ once: true, amount: 0.5 }}
          transition={{ duration: 0.7 }}
        >
          <h2 className="text-4xl sm:text-5xl md:text-6xl font-bold text-black tracking-tighter leading-tight">
            영상 속 모든 순간이<br />
            구매가 됩니다.
          </h2>
          <p className="mt-6 text-lg md:text-xl text-gray-600 max-w-xl font-light leading-relaxed">
            Clozet은 시청자가 영상을 즐기는 동시에 자연스럽게 상품을 발견하고 구매할 수 있는 가장 직관적인 방법을 제안합니다.
          </p>
          <ul className="mt-10 space-y-4">
            {features.map((feature, index) => (
              <li key={index} className="flex items-start gap-3">
                <CheckCircleIcon className="w-6 h-6 text-blue-500 flex-shrink-0 mt-1" />
                <span className="text-lg md:text-xl text-black font-light leading-relaxed">
                  {feature}
                </span>
              </li>
            ))}
          </ul>
        </motion.div>
      </div>
    </section>
  );
};

// --- 5f. Feature 섹션 3 (세 번째 섹션) ---
const FeatureSection3: React.FC<FeatureSectionProps> = ({ activeTab, stepNumber = 3 }) => {
  // 크리에이터, 인플루언서, 브랜드 탭일 때만 표시
  if (activeTab !== 'creators' && activeTab !== 'influencers' && activeTab !== 'brands') {
    return null;
  }

  const bgColor = stepNumber % 2 === 1 ? 'bg-gray-50' : 'bg-white';

  // 이미지 경로 설정
  const imageSrc = '/screenshots/settlement.png';

  return (
    <section className={`py-20 md:py-32 ${bgColor}`}>
      <div className="container mx-auto px-6 md:px-16">
        <div className="grid lg:grid-cols-2 gap-16 lg:gap-24 items-center">
          {/* 좌측: 텍스트 */}
          <motion.div
            initial={{ opacity: 0, x: -30 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true, amount: 0.5 }}
            transition={{ duration: 0.7 }}
          >
            <div className="space-y-6">
              <div className="inline-block px-4 py-2 bg-black text-white rounded-full text-sm font-medium mb-4">
                Step {stepNumber}
              </div>
              <h3 className="text-2xl md:text-3xl font-bold text-black tracking-tight leading-tight mb-3">
                Profit Dashboard
              </h3>
              {activeTab === 'brands' ? (
                <>
                  <h4 className="text-3xl md:text-4xl font-bold text-black tracking-tight leading-tight">
                    좋아요 수 말고,<br />매출을 확인하세요
                  </h4>
                  <p className="text-xl md:text-2xl text-gray-600 font-light leading-relaxed whitespace-pre-line">
                    <strong className="text-black">크리에이터별 판매 실적</strong>과 수수료 정산 내역이 투명하게 공개됩니다.
                  </p>
                  <div className="bg-gradient-to-r from-green-50 to-emerald-50 border-l-4 border-green-500 p-6 rounded-lg mt-8">
                    <div className="flex items-start gap-4">
                      <div className="text-3xl">💰</div>
                      <div>
                        <h4 className="text-lg font-semibold text-black mb-2">투명한 정산 시스템</h4>
                        <p className="text-base text-gray-600 font-light">
                          실시간 대시보드에서 판매 현황을 확인하고, 매주 월요일 자동 정산으로 빠르게 정산을 완료하세요.
                        </p>
                      </div>
                    </div>
                  </div>
                </>
              ) : (
                <>
                  <h4 className="text-3xl md:text-4xl font-bold text-black tracking-tight leading-tight">
                    좋아요 수 말고,<br />통장 잔고를 확인하세요
                  </h4>
                  <p className="text-xl md:text-2xl text-gray-600 font-light leading-relaxed whitespace-pre-line">
                    <strong className="text-black">판매된 금액의 N%</strong>가{'\n'}
                    투명하게 정산되어{'\n'}
                    매주 입금됩니다.
                  </p>
                  <div className="bg-gradient-to-r from-green-50 to-emerald-50 border-l-4 border-green-500 p-6 rounded-lg mt-8">
                    <div className="flex items-start gap-4">
                      <div className="text-3xl">💰</div>
                      <div>
                        <h4 className="text-lg font-semibold text-black mb-2">투명한 정산 시스템</h4>
                        <p className="text-base text-gray-600 font-light">
                          실시간 대시보드에서 판매 현황을 확인하고, 매주 월요일 자동 정산으로 빠르게 수익을 받아보세요.
                        </p>
                      </div>
                    </div>
                  </div>
                </>
              )}
            </div>
          </motion.div>

          {/* 우측: 노트북 프레임 */}
          <motion.div
            initial={{ opacity: 0, scale: 0.9 }}
            whileInView={{ opacity: 1, scale: 1 }}
            viewport={{ once: true, amount: 0.5 }}
            transition={{ duration: 0.7 }}
          >
            <LaptopFrame imageSrc={imageSrc} />
          </motion.div>
        </div>
      </div>
    </section>
  );
};

// --- 5g. Feature 섹션 4 (네 번째 섹션) ---
const FeatureSection4: React.FC<FeatureSectionProps> = ({ activeTab, stepNumber = 4 }) => {
  // 크리에이터, 인플루언서, 브랜드 탭일 때만 표시
  if (activeTab !== 'creators' && activeTab !== 'influencers' && activeTab !== 'brands') {
    return null;
  }

  const bgColor = stepNumber % 2 === 1 ? 'bg-gray-50' : 'bg-white';

  // 이미지 경로 설정
  const getImageSrc = () => {
    if (activeTab === 'brands') {
      return '/screenshots/brands_checkout_iphone.png';
    }
    return '/screenshots/creators_infoshare_iphone.png';
  };

  const getVideoSrc = () => {
    return '/videos/Clozet_Demo_InstagramShare_LandingPage_onlyScreen.mp4';
  };

  return (
    <section className={`py-20 md:py-32 ${bgColor}`}>
      <div className="container mx-auto px-6 md:px-16">
        {/* 상단 타이틀 - 첫 번째일 때 */}
        {stepNumber === 1 && (
          <motion.h2
            className="text-4xl sm:text-5xl md:text-6xl font-bold text-black tracking-tighter leading-tight text-center mb-20"
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, amount: 0.5 }}
            transition={{ duration: 0.7 }}
          >
            {activeTab === 'brands' ? (
              <>
                상품만 등록하세요.<br />세일즈는 <span className="text-transparent bg-clip-text bg-gradient-to-r from-purple-600 to-blue-600">크리에이터</span>가 대신합니다
              </>
            ) : activeTab === 'influencers' ? (
              <>
                '이 옷 뭐예요?' 수백 번 묻는 팬들에게,<br /><span className="text-transparent bg-clip-text bg-gradient-to-r from-purple-600 to-blue-600">"이 릴스를 Clozet으로 공유해주세요"</span><br />문장 하나로 답장하세요.
              </>
            ) : (
              <>
                재고 걱정, 배송 부담 없이<br />당신의 <span className="text-transparent bg-clip-text bg-gradient-to-r from-purple-600 to-blue-600">'안목'</span>을 팝니다
              </>
            )}
          </motion.h2>
        )}

        <div className="grid lg:grid-cols-2 gap-16 lg:gap-24 items-center">
          {/* 좌측: 아이폰 프레임 또는 비디오 */}
          <motion.div
            initial={{ opacity: 0, scale: 0.9 }}
            whileInView={{ opacity: 1, scale: 1 }}
            viewport={{ once: true, amount: 0.5 }}
            transition={{ duration: 0.7 }}
          >
            {getVideoSrc() ? (
              <div className="relative w-[280px] md:w-[320px] mx-auto bg-black rounded-[40px] p-3 shadow-2xl">
                {/* 노치 */}
                <div className="absolute top-0 left-1/2 transform -translate-x-1/2 w-32 h-6 bg-black rounded-b-2xl z-10"></div>

                {/* 스크린 영역 */}
                <div className="relative bg-white rounded-[32px] overflow-hidden">
                  <video
                    src={getVideoSrc()!}
                    autoPlay
                    muted
                    loop
                    playsInline
                    className="w-full h-auto"
                  />
                </div>
              </div>
            ) : (
              <IPhoneFrame imageSrc={getImageSrc()} />
            )}
          </motion.div>

          {/* 우측: 텍스트 */}
          <motion.div
            initial={{ opacity: 0, x: 30 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true, amount: 0.5 }}
            transition={{ duration: 0.7 }}
          >
            <div className="space-y-6">
              <div className="inline-block px-4 py-2 bg-black text-white rounded-full text-sm font-medium mb-4">
                Step {stepNumber}
              </div>
              {activeTab === 'brands' ? (
                <>
                  <h3 className="text-2xl md:text-3xl font-bold text-black tracking-tight leading-tight mb-3">
                    Zero-Friction Checkout
                  </h3>
                  <h4 className="text-3xl md:text-4xl font-bold text-black tracking-tight leading-tight">
                    이탈 없는 결제
                  </h4>
                  <p className="text-xl md:text-2xl text-gray-600 font-light leading-relaxed whitespace-pre-line">
                    인스타그램 '공유하기' 버튼 하나로 구매 페이지가 열립니다. 프로필 링크를 찾아 헤매던 고객을 <strong className="text-black">탭 한번으로 결제{'\n'}인스타그램에서 손쉽게 구매 전환</strong>까지 이끄세요.
                  </p>
                  <div className="flex items-center gap-3 pt-4">
                    <CheckCircleIcon className="w-6 h-6 text-green-500 flex-shrink-0" />
                    <span className="text-lg text-gray-600">인스타그램에서 손쉽게 구매 전환</span>
                  </div>
                  <div className="flex items-center gap-3">
                    <CheckCircleIcon className="w-6 h-6 text-green-500 flex-shrink-0" />
                    <span className="text-lg text-gray-600">최소 클릭으로 최대 전환율 달성</span>
                  </div>
                </>
              ) : (
                <>
                  <h3 className="text-2xl md:text-3xl font-bold text-black tracking-tight leading-tight mb-3">
                    One-Tap Info Share
                  </h3>
                  <h4 className="text-3xl md:text-4xl font-bold text-black tracking-tight leading-tight">
                    정보 공유 자동화
                  </h4>
                  <p className="text-xl md:text-2xl text-gray-600 font-light leading-relaxed">
                    일일이 DM 보내지 마세요. 릴스를 Clozet으로 공유하면, 팬들이 영상 속 정보를 스스로 확인하고 구매합니다.
                  </p>
                  <div className="flex items-center gap-3 pt-4">
                    <CheckCircleIcon className="w-6 h-6 text-blue-500 flex-shrink-0" />
                    <span className="text-lg text-gray-600">댓글이나 DM 없이, 영상 속에서 바로 상품을 확인하고 구매까지</span>
                  </div>
                  <div className="flex items-center gap-3">
                    <CheckCircleIcon className="w-6 h-6 text-blue-500 flex-shrink-0" />
                    <span className="text-lg text-gray-600">팬들의 편의성 향상으로 만족도 UP</span>
                  </div>
                </>
              )}
            </div>
          </motion.div>
        </div>
      </div>
    </section>
  );
};

// --- 5h. Feature 섹션 5 (다섯 번째 섹션) ---
const FeatureSection5: React.FC<FeatureSectionProps> = ({ activeTab, stepNumber = 5 }) => {
  // 크리에이터, 인플루언서, 브랜드 탭일 때만 표시
  if (activeTab !== 'creators' && activeTab !== 'influencers' && activeTab !== 'brands') {
    return null;
  }

  const bgColor = stepNumber % 2 === 1 ? 'bg-gray-50' : 'bg-white';

  // 이미지 경로 설정
  const getImageSrc = () => {
    if (activeTab === 'brands') {
      return '/screenshots/dashboard_laptop.png';
    }
    return '/screenshots/creators_passive_iphone.png';
  };

  return (
    <section className={`py-20 md:py-32 ${bgColor}`}>
      <div className="container mx-auto px-6 md:px-16">
        <div className="grid lg:grid-cols-2 gap-16 lg:gap-24 items-center">
          {/* 좌측: 텍스트 */}
          <motion.div
            initial={{ opacity: 0, x: -30 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true, amount: 0.5 }}
            transition={{ duration: 0.7 }}
          >
            <div className="space-y-6">
              <div className="inline-block px-4 py-2 bg-black text-white rounded-full text-sm font-medium mb-4">
                Step {stepNumber}
              </div>
              {activeTab === 'brands' ? (
                <>
                  <h3 className="text-2xl md:text-3xl font-bold text-black tracking-tight leading-tight mb-3">
                    Real-Time Dashboard
                  </h3>
                  <h4 className="text-3xl md:text-4xl font-bold text-black tracking-tight leading-tight">
                    데이터 투명성
                  </h4>
                  <p className="text-xl md:text-2xl text-gray-600 font-light leading-relaxed">
                    누가, 어떤 영상에서, 얼마나 팔았는지 실시간으로 확인하세요. <strong className="text-black">구매 전환율(CVR)</strong>과 매출 데이터를 투명하게 제공합니다.
                  </p>
                  <div className="bg-gradient-to-r from-blue-50 to-indigo-50 border-l-4 border-blue-500 p-6 rounded-lg mt-8">
                    <div className="flex items-start gap-4">
                      <div className="text-3xl">📊</div>
                      <div>
                        <h4 className="text-lg font-semibold text-black mb-2">실시간 성과 분석</h4>
                        <p className="text-base text-gray-600 font-light">
                          어떤 크리에이터가 어떤 상품을 잘 파는지, 데이터 기반으로 마케팅 전략을 수립하세요.
                        </p>
                      </div>
                    </div>
                  </div>
                </>
              ) : (
                <>
                  <h3 className="text-2xl md:text-3xl font-bold text-black tracking-tight leading-tight mb-3">
                    Passive Income
                  </h3>
                  <h4 className="text-3xl md:text-4xl font-bold text-black tracking-tight leading-tight">
                    잠자는 동안의 수익
                  </h4>
                  <p className="text-xl md:text-2xl text-gray-600 font-light leading-relaxed whitespace-pre-line">
                    한 번 공유해둔 영상은 24시간 일합니다.{'\n'}
                    팬들이 정보를 확인하고{'\n'}
                    구매할 때마다 당신에게 보너스 수익이 쌓입니다.
                  </p>
                  <div className="bg-gradient-to-r from-purple-50 to-indigo-50 border-l-4 border-purple-500 p-6 rounded-lg mt-8">
                    <div className="flex items-start gap-4">
                      <div className="text-3xl">🌙</div>
                      <div>
                        <h4 className="text-lg font-semibold text-black mb-2">24/7 자동 수익</h4>
                        <p className="text-base text-gray-600 font-light">
                          콘텐츠를 한 번만 올려두면, 시간과 장소에 구애받지 않고 지속적으로 수익이 발생합니다.
                        </p>
                      </div>
                    </div>
                  </div>
                </>
              )}
            </div>
          </motion.div>

          {/* 우측: 프레임 (브랜드는 노트북, 나머지는 아이폰) */}
          <motion.div
            initial={{ opacity: 0, scale: 0.9 }}
            whileInView={{ opacity: 1, scale: 1 }}
            viewport={{ once: true, amount: 0.5 }}
            transition={{ duration: 0.7 }}
          >
            {activeTab === 'brands' ? <LaptopFrame imageSrc={getImageSrc()} /> : <IPhoneFrame imageSrc={getImageSrc()} />}
          </motion.div>
        </div>
      </div>
    </section>
  );
};

// --- 6. FAQ 섹션 (새로 추가) ---

// FAQ 아코디언 아이템
interface FAQItemProps {
  question: string;
  answer: string;
  isOpen: boolean;
  onClick: () => void;
}

const FAQItem: React.FC<FAQItemProps> = ({ question, answer, isOpen, onClick }) => {
  return (
    <div className="border-b border-gray-200">
      <button
        className="flex justify-between items-center w-full py-6 text-left"
        onClick={onClick}
      >
        <span className="text-lg md:text-2xl font-medium text-black">{question}</span>
        {isOpen ? <MinusIcon className="w-6 h-6 text-black flex-shrink-0" /> : <PlusIcon className="w-6 h-6 text-gray-400 flex-shrink-0" />}
      </button>
      <AnimatePresence>
        {isOpen && (
          <motion.div
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: "auto" }}
            exit={{ opacity: 0, height: 0 }}
            transition={{ duration: 0.3, ease: "easeInOut" }}
            className="overflow-hidden pb-6"
          >
            <p className="p-4 text-lg md:text-xl text-white font-normal leading-relaxed max-w-3xl bg-black rounded-lg whitespace-pre-line">
              {answer}
            </p>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};

const FAQSection: React.FC = () => {
  const [openIndex, setOpenIndex] = useState<number | null>(null);
  const faqs = [
    { q: "입점 비용은 얼마인가요?", a: "Clozet은 입점 비용 없이 판매 수수료 모델을 기반으로 합니다.\n초기 비용 부담 없이 시작하세요.\n\n카테고리별 판매 수수료:\n• 소호 브랜드 3%\n• 대형 브랜드 10%\n• 뷰티 10%\n• 리빙 / 홈 8%" },
    { q: "수익 정산은 어떻게 이루어지나요?", a: "매주 월요일, 구매 확정된 건에 한하여 정산이 이루어집니다.\n상세 내용은 입점 시 제공되는 파트너 가이드를 참고해주세요." },
    { q: "영상 콘텐츠는 누가 제작하나요?", a: "브랜드가 직접 제작하여 업로드할 수도 있고, 크리에이터/인플루언서와 협업을 통해\n제품을 판매 할 수 있습니다." },
    { q: "데이터 대시보드에서는 무엇을 볼 수 있나요?", a: "영상별 조회수, 클릭률, 구매 전환율, 스티커별 성과 등 커머스에\n최적화된 상세 데이터를 제공합니다." },
  ];

  const handleClick = (index: number) => {
    setOpenIndex(openIndex === index ? null : index);
  };

  return (
    <section className="py-20 md:py-32 bg-gray-50">
      <div className="container mx-auto px-6 md:px-16 max-w-4xl">
        <motion.h2
          className="text-4xl sm:text-5xl md:text-6xl font-bold text-black tracking-tighter leading-tight text-center mb-16"
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, amount: 0.5 }}
          transition={{ duration: 0.7 }}
        >
          Frequently Asked Questions
        </motion.h2>
        <div className="w-full">
          {faqs.map((faq, index) => (
            <FAQItem
              key={index}
              question={faq.q}
              answer={faq.a}
              isOpen={openIndex === index}
              onClick={() => handleClick(index)}
            />
          ))}
        </div>
      </div>
    </section>
  );
};


// --- 8. CTA 섹션 ---

interface CTASectionProps {
  activeTab: TabType | null;
  language: LanguageType;
}

const CTASection: React.FC<CTASectionProps> = ({ activeTab }) => {
  const handleStartClick = () => {
    window.location.href = 'https://tally.so/r/zxMWQM';
  };

  // 탭별 메시지 정의
  const getTabMessage = () => {
    if (!activeTab) return null;

    switch (activeTab) {
      case 'brands':
        return {
          title: '브랜드는 제품에만 집중하세요.',
          description: '마케팅은 걱정 마세요.\n판매는 크리에이터 군단이 그들이 제일 잘 하는방식으로 합니다.'
        };
      case 'creators':
        return {
          title: '크리에이터 여러분, 안목을 수익으로 바꾸세요.',
          description: '당신의 큐레이션이 곧 돈이 됩니다.\n재고 걱정 없는 나만의 컨텐츠 편집숍을 지금 바로 시작하세요.'
        };
      case 'influencers':
        return {
          title: '인플루언서 여러분, 주도적인 수익을 만드세요.',
          description: '광고주 연락만 기다리지 마세요.\n내가 입고 쓰는 모든 제품이 매일매일 수익이 됩니다.'
        };
    }
  };

  const tabMessage = getTabMessage();

  return (
    <motion.section
      className="py-20 md:py-32 bg-white"
      initial={{ opacity: 0 }}
      whileInView={{ opacity: 1 }}
      viewport={{ once: true, amount: 0.5 }}
      transition={{ duration: 0.8 }}
    >
      <div className="container mx-auto px-6 md:px-16 text-center flex flex-col items-center">
        <h2 className="text-3xl sm:text-4xl md:text-6xl font-bold text-black tracking-tighter leading-tight max-w-4xl">
          지금 Clozet에서<br />
          여러분의 재능을 '매출'로 증명하세요
        </h2>

        {/* 탭별 메시지 표시 */}
        {tabMessage && (
          <motion.div
            className="mt-12 max-w-2xl"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5 }}
          >
            <h3 className="text-3xl md:text-4xl font-bold text-black mb-4">
              {tabMessage.title}
            </h3>
            <p className="text-xl md:text-2xl text-gray-600 font-normal leading-relaxed whitespace-pre-line">
              {tabMessage.description}
            </p>
          </motion.div>
        )}

        <motion.button
          onClick={handleStartClick}
          className="mt-10 text-lg font-light py-3 px-10 bg-gradient-to-br from-gray-900 via-black to-gray-900 text-white rounded-full shadow-[0_8px_16px_rgba(0,0,0,0.3),0_4px_8px_rgba(0,0,0,0.2)] hover:shadow-[0_12px_24px_rgba(0,0,0,0.4),0_6px_12px_rgba(0,0,0,0.3)] transition-all duration-300 border border-gray-700 relative overflow-hidden before:absolute before:inset-0 before:bg-gradient-to-b before:from-white/20 before:via-transparent before:to-transparent before:opacity-0 hover:before:opacity-100 before:transition-opacity"
          whileHover={{ scale: 1.05, y: -2 }}
          whileTap={{ scale: 0.95, y: 0 }}
        >
          무료로 시작하기
        </motion.button>

        {/* Quote 형식 하이라이트 - 파란색 드래그 효과 */}
        <motion.blockquote
          className="mt-16 p-8 bg-blue-100 border-l-4 border-blue-500 rounded-lg max-w-3xl"
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.5 }}
        >
          <p className="text-xl md:text-2xl text-blue-900 font-light italic leading-relaxed">
            "유튜브와 인스타그램의 초창기를 놓치셨나요?<br />
            이번 숏폼 커머스의 파도만큼은 놓치지 마세요"
          </p>
        </motion.blockquote>
      </div>
    </motion.section>
  );
};


// --- 9. Footer 컴포넌트 ---

const Footer: React.FC = () => {
  return (
    <footer className="py-16 md:py-24 bg-gray-50 border-t border-gray-200">
      <div className="container mx-auto px-6 md:px-16">
        <div className="grid grid-cols-1 md:grid-cols-3 gap-12">
          {/* 좌측: 회사 정보 */}
          <div>
            <img src={logo} alt="Clozet" className="h-8 mb-4" />
            <p className="text-gray-600 font-light mb-2">
              The Next Generation of Video Commerce.
            </p>

            {/* 소셜 미디어 링크 */}
            <div className="flex gap-4 mt-6">
              <a
                href="https://www.instagram.com/clozet_official"
                target="_blank"
                rel="noopener noreferrer"
                className="w-10 h-10 bg-white border border-gray-200 rounded-full flex items-center justify-center hover:bg-pink-50 hover:border-pink-300 transition-all"
                aria-label="Instagram"
              >
                <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 24 24">
                  <path d="M12 2.163c3.204 0 3.584.012 4.85.07 3.252.148 4.771 1.691 4.919 4.919.058 1.265.069 1.645.069 4.849 0 3.205-.012 3.584-.069 4.849-.149 3.225-1.664 4.771-4.919 4.919-1.266.058-1.644.07-4.85.07-3.204 0-3.584-.012-4.849-.07-3.26-.149-4.771-1.699-4.919-4.92-.058-1.265-.07-1.644-.07-4.849 0-3.204.013-3.583.07-4.849.149-3.227 1.664-4.771 4.919-4.919 1.266-.057 1.645-.069 4.849-.069zm0-2.163c-3.259 0-3.667.014-4.947.072-4.358.2-6.78 2.618-6.98 6.98-.059 1.281-.073 1.689-.073 4.948 0 3.259.014 3.668.072 4.948.2 4.358 2.618 6.78 6.98 6.98 1.281.058 1.689.072 4.948.072 3.259 0 3.668-.014 4.948-.072 4.354-.2 6.782-2.618 6.979-6.98.059-1.28.073-1.689.073-4.948 0-3.259-.014-3.667-.072-4.947-.196-4.354-2.617-6.78-6.979-6.98-1.281-.059-1.69-.073-4.949-.073zm0 5.838c-3.403 0-6.162 2.759-6.162 6.162s2.759 6.163 6.162 6.163 6.162-2.759 6.162-6.163c0-3.403-2.759-6.162-6.162-6.162zm0 10.162c-2.209 0-4-1.79-4-4 0-2.209 1.791-4 4-4s4 1.791 4 4c0 2.21-1.791 4-4 4zm6.406-11.845c-.796 0-1.441.645-1.441 1.44s.645 1.44 1.441 1.44c.795 0 1.439-.645 1.439-1.44s-.644-1.44-1.439-1.44z"/>
                </svg>
              </a>
              <a
                href="https://apps.apple.com/kr/app/clozet/id6752570305?l=en-GB"
                target="_blank"
                rel="noopener noreferrer"
                className="w-10 h-10 bg-white border border-gray-200 rounded-full flex items-center justify-center hover:bg-gray-100 hover:border-gray-300 transition-all"
                aria-label="App Store"
              >
                <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 24 24">
                  <path d="M18.71 19.5c-.83 1.24-1.71 2.45-3.05 2.47-1.34.03-1.77-.79-3.29-.79-1.53 0-2 .77-3.27.82-1.31.05-2.3-1.32-3.14-2.53C4.25 17 2.94 12.45 4.7 9.39c.87-1.52 2.43-2.48 4.12-2.51 1.28-.02 2.5.87 3.29.87.78 0 2.26-1.07 3.81-.91.65.03 2.47.26 3.64 1.98-.09.06-2.17 1.28-2.15 3.81.03 3.02 2.65 4.03 2.68 4.04-.03.07-.42 1.44-1.38 2.83M13 3.5c.73-.83 1.94-1.46 2.94-1.5.13 1.17-.34 2.35-1.04 3.19-.69.85-1.83 1.51-2.95 1.42-.15-1.15.41-2.35 1.05-3.11z"/>
                </svg>
              </a>
            </div>
          </div>

          {/* 중앙: 정책 및 링크 */}
          <div>
            <h3 className="font-bold text-black mb-4">정책</h3>
            <div className="flex flex-col gap-3">
              <a
                href="/terms/service"
                className="text-gray-600 hover:text-black transition-colors font-light"
              >
                이용약관
              </a>
              <a
                href="/terms/privacy"
                className="text-gray-600 hover:text-black transition-colors font-light"
              >
                개인정보 처리방침
              </a>
            </div>
          </div>

          {/* 우측: 사업자 정보 */}
          <div>
            <h3 className="font-bold text-black mb-4">사업자 정보</h3>
            <div className="text-sm text-gray-600 font-light space-y-2">
              <p>상호명: 클로젯</p>
              <p>대표자: 김재원</p>
              <p>사업자등록번호: 515-09-67374</p>
              <p>통신판매업신고번호: [신고번호]</p>
              <p>주소: 서울시 송파구 송파대로 567</p>
              <p className="mt-4">
                이메일: <a href="mailto:support@clozet.my" className="hover:text-black transition-colors">support@clozet.my</a>
              </p>
            </div>
          </div>
        </div>

        {/* 하단 카피라이트 */}
        <div className="mt-12 pt-8 border-t border-gray-200 text-center">
          <p className="text-gray-500 font-light text-sm">
            © {new Date().getFullYear()} Clozet Inc. All rights reserved.
          </p>
        </div>
      </div>
    </footer>
  );
};


// --- LandingPage 컴포넌트 (메인) ---

const LandingPage: React.FC = () => {
  const [activeTab, setActiveTab] = useState<TabType | null>(null);
  const [language, setLanguage] = useState<LanguageType>('ko');
  const tabsSectionRef = useRef<HTMLDivElement>(null);

  const scrollToTabs = () => {
    tabsSectionRef.current?.scrollIntoView({
      behavior: 'smooth',
      block: 'start',
    });
  };

  return (
    <Fragment>
      {/* 메인 앱 레이아웃 */}
      <div className="bg-white text-black min-h-screen">
        <Navbar
          activeTab={activeTab}
          setActiveTab={setActiveTab}
          scrollToTabs={scrollToTabs}
          language={language}
          setLanguage={setLanguage}
        />

        <main>
          <Hero
            activeTab={activeTab}
            setActiveTab={setActiveTab}
            scrollToTabs={scrollToTabs}
            language={language}
          />
          {activeTab && (
            <>
              <TabsSection
                activeTab={activeTab}
                tabsSectionRef={tabsSectionRef}
                language={language}
              />
              {/* <PartnerLogos /> */}

              {/* 크리에이터: 1-4-5-2-3 순서 */}
              {activeTab === 'creators' && (
                <>
                  <FeatureSection activeTab={activeTab} stepNumber={1} language={language} />
                  <FeatureSection4 activeTab={activeTab} stepNumber={2} language={language} />
                  <FeatureSection5 activeTab={activeTab} stepNumber={3} language={language} />
                  <FeatureSection2 activeTab={activeTab} stepNumber={4} language={language} />
                  <FeatureSection3 activeTab={activeTab} stepNumber={5} language={language} />
                </>
              )}

              {/* 인플루언서: 4-2-1-5-3 순서 */}
              {activeTab === 'influencers' && (
                <>
                  <FeatureSection4 activeTab={activeTab} stepNumber={1} language={language} />
                  <FeatureSection2 activeTab={activeTab} stepNumber={2} language={language} />
                  <FeatureSection activeTab={activeTab} stepNumber={3} language={language} />
                  <FeatureSection5 activeTab={activeTab} stepNumber={4} language={language} />
                  <FeatureSection3 activeTab={activeTab} stepNumber={5} language={language} />
                </>
              )}

              {/* 브랜드: 5개 섹션 (4-2-1-5-3 순서) */}
              {activeTab === 'brands' && (
                <>
                  <FeatureSection4 activeTab={activeTab} stepNumber={1} language={language} />
                  <FeatureSection2 activeTab={activeTab} stepNumber={2} language={language} />
                  <FeatureSection activeTab={activeTab} stepNumber={3} language={language} />
                  <FeatureSection5 activeTab={activeTab} stepNumber={4} language={language} />
                  <FeatureSection3 activeTab={activeTab} stepNumber={5} language={language} />
                </>
              )}

              <CTASection activeTab={activeTab} language={language} />
              <FAQSection />
            </>
          )}
        </main>

        <Footer />
      </div>
    </Fragment>
  );
};

export default LandingPage;
